import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowLeft, Save, Eye, Loader2, Users } from "lucide-react";
import { toast } from "sonner";
import { SectionCard, FieldRow, TextAreaRow, GalleryEditor, StringListEditor, SimpleAchievementEditor } from "@/components/EntityEditShared";
import { EntityLinksManager } from "@/components/EntityLinks";
import { PalmaresAIOrganizer } from "@/components/PalmaresAIOrganizer";
import { showError } from "@/lib/errors";

export default function CoachEditPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [coach, setCoach] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [disciplines, setDisciplines] = useState([]);

  useEffect(() => {
    api.get("/disciplines").then((r) => setDisciplines(r.data.items || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate(`/connexion?next=/coachs/${id}/edit`); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/coaches/${id}`);
        if (cancelled) return;
        const ok = user.id === data.owner_user_id || (user.roles || []).some((r) => ["admin", "copf"].includes(r));
        if (!ok) { toast.error("Accès réservé au propriétaire."); navigate(`/coachs/${data.slug}`); return; }
        setCoach(data);
      } catch (e) {
        if (e?.response?.status === 404) navigate("/coachs", { replace: true });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id, user, authLoading, navigate]);

  const upd = (patch) => setCoach((c) => ({ ...c, ...patch }));

  const save = async () => {
    setSaving(true);
    try {
      const fields = {
        full_name: coach.full_name, photo_url: coach.photo_url, cover_url: coach.cover_url,
        bio: coach.bio, short_motto: coach.short_motto,
        disciplines: coach.disciplines, island: coach.island, commune: coach.commune,
        diplomas: coach.diplomas, experience_years: coach.experience_years,
        contact_email: coach.contact_email, contact_phone: coach.contact_phone, website: coach.website,
        social_facebook: coach.social_facebook, social_instagram: coach.social_instagram,
        gallery: coach.gallery, achievements: coach.achievements, published: coach.published,
      };
      const { data } = await api.patch(`/coaches/${coach.id}`, fields);
      setCoach(data);
      toast.success("Fiche coach sauvegardée.");
    } catch (e) {
      showError(e, { fallback: "Échec de la sauvegarde" });
    } finally { setSaving(false); }
  };

  if (loading || !coach) return <div className="max-w-4xl mx-auto py-8 px-4 space-y-3"><Skeleton className="h-12 w-1/2" /><Skeleton className="h-64 w-full" /></div>;

  return (
    <div className="max-w-4xl mx-auto py-6 px-4 space-y-5" data-testid="coach-edit-page">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          <Link to={`/coachs/${coach.slug}`}><Button variant="ghost" size="sm" data-testid="coach-edit-back"><ArrowLeft className="h-4 w-4 mr-1" />Retour</Button></Link>
          <h1 className="font-display text-xl sm:text-2xl font-bold truncate flex items-center gap-2"><Users className="h-5 w-5 text-[hsl(var(--aito-lagoon))]" />Édition · {coach.full_name}</h1>
        </div>
        <div className="flex items-center gap-2">
          <Link to={`/coachs/${coach.slug}`}><Button variant="outline" size="sm" data-testid="coach-edit-preview"><Eye className="h-4 w-4 mr-1.5" />Voir la fiche</Button></Link>
          <Button size="sm" onClick={save} disabled={saving} data-testid="coach-edit-save" className="bg-[hsl(var(--aito-lagoon))] hover:bg-[hsl(var(--aito-lagoon-2))] text-black">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Save className="h-4 w-4 mr-1.5" />}
            Sauvegarder
          </Button>
        </div>
      </div>

      <Tabs defaultValue="identity">
        <TabsList data-testid="coach-edit-tabs">
          <TabsTrigger value="identity" data-testid="coach-edit-tab-identity">Identité</TabsTrigger>
          <TabsTrigger value="contact" data-testid="coach-edit-tab-contact">Contact</TabsTrigger>
          <TabsTrigger value="network" data-testid="coach-edit-tab-network">Réseau</TabsTrigger>
          <TabsTrigger value="palmares" data-testid="coach-edit-tab-palmares">Palmarès personnel</TabsTrigger>
          <TabsTrigger value="gallery" data-testid="coach-edit-tab-gallery">Galerie</TabsTrigger>
        </TabsList>

        <TabsContent value="identity" className="mt-4 space-y-4">
          <SectionCard title="Identité" description="Informations principales.">
            <div className="grid sm:grid-cols-2 gap-3">
              <FieldRow label="Nom complet" value={coach.full_name} onChange={(v) => upd({ full_name: v })} placeholder="Prénom Nom" testid="coach-field-name" />
              <FieldRow label="Baseline / devise" value={coach.short_motto} onChange={(v) => upd({ short_motto: v })} placeholder="Coacher c'est transmettre" testid="coach-field-motto" />
              <FieldRow label="Photo (URL)" value={coach.photo_url} onChange={(v) => upd({ photo_url: v })} placeholder="https://...portrait.jpg" testid="coach-field-photo" />
              <FieldRow label="Image de couverture (URL)" value={coach.cover_url} onChange={(v) => upd({ cover_url: v })} placeholder="https://...cover.jpg" testid="coach-field-cover" />
              <FieldRow label="Île" value={coach.island} onChange={(v) => upd({ island: v })} placeholder="Tahiti" testid="coach-field-island" />
              <FieldRow label="Commune" value={coach.commune} onChange={(v) => upd({ commune: v })} placeholder="Papeete" testid="coach-field-commune" />
              <FieldRow label="Années d'expérience" type="number" value={coach.experience_years || ""} onChange={(v) => upd({ experience_years: v ? parseInt(v, 10) : null })} placeholder="12" testid="coach-field-exp" />
              <div className="flex items-center gap-2 pt-5">
                <input id="published" type="checkbox" checked={!!coach.published} onChange={(e) => upd({ published: e.target.checked })} data-testid="coach-field-published" className="h-4 w-4" />
                <label htmlFor="published" className="text-sm cursor-pointer">Fiche publiée (visible dans l'annuaire)</label>
              </div>
            </div>
          </SectionCard>
          <SectionCard title="Disciplines" description="Spécialités du coach.">
            <StringListEditor items={coach.disciplines || []} onChange={(v) => upd({ disciplines: v })} placeholder="Ex. surf, va'a, boxe, athlétisme…" testid="coach-disciplines" />
          </SectionCard>
          <SectionCard title="Diplômes et certifications" description="Brevet d'État, ceintures, certifications fédérales.">
            <StringListEditor items={coach.diplomas || []} onChange={(v) => upd({ diplomas: v })} placeholder="Ex. BEES, diplôme fédéral, formation continue…" testid="coach-diplomas" />
          </SectionCard>
          <SectionCard title="Biographie" description="Parcours, philosophie, vision.">
            <TextAreaRow label="Bio" value={coach.bio} onChange={(v) => upd({ bio: v })} placeholder="Pratiquant depuis..." rows={6} testid="coach-field-bio" />
          </SectionCard>
        </TabsContent>

        <TabsContent value="contact" className="mt-4 space-y-4">
          <SectionCard title="Coordonnées" description="Comment vous contacter.">
            <div className="grid sm:grid-cols-2 gap-3">
              <FieldRow label="Email" type="email" value={coach.contact_email} onChange={(v) => upd({ contact_email: v })} placeholder="coach@email.pf" testid="coach-field-email" />
              <FieldRow label="Téléphone" value={coach.contact_phone} onChange={(v) => upd({ contact_phone: v })} placeholder="+689 ..." testid="coach-field-phone" />
              <FieldRow label="Site web" value={coach.website} onChange={(v) => upd({ website: v })} placeholder="https://..." testid="coach-field-website" />
              <FieldRow label="Facebook" value={coach.social_facebook} onChange={(v) => upd({ social_facebook: v })} placeholder="https://facebook.com/..." testid="coach-field-fb" />
              <FieldRow label="Instagram" value={coach.social_instagram} onChange={(v) => upd({ social_instagram: v })} placeholder="https://instagram.com/..." testid="coach-field-ig" />
            </div>
          </SectionCard>
        </TabsContent>

        <TabsContent value="network" className="mt-4 space-y-4">
          <SectionCard title="Clubs d'attache" description="Déclare officiellement le ou les clubs avec lesquels tu travailles. Le lien devient public après validation par le club cible.">
            <EntityLinksManager sourceType="coach" sourceId={coach.id} />
          </SectionCard>
        </TabsContent>

        <TabsContent value="palmares" className="mt-4 space-y-4">
          <SectionCard
            title="Palmarès personnel"
            description="Titres remportés en tant que compétiteur ou coach (formation d'athlètes médaillés, etc.)."
            action={
              <PalmaresAIOrganizer
                coachId={coach.id}
                entityType="coach"
                discipline={coach.main_discipline || (coach.disciplines || [])[0]}
                disciplines={disciplines}
                onCommitted={(updated) => updated && setCoach(updated)}
                label="Organiser avec l'IA"
              />
            }
          >
            <SimpleAchievementEditor items={coach.achievements || []} onChange={(v) => upd({ achievements: v })} />
          </SectionCard>
        </TabsContent>

        <TabsContent value="gallery" className="mt-4">
          <SectionCard title="Galerie photos" description="Photos d'entraînement, compétitions, cérémonies.">
            <GalleryEditor items={coach.gallery || []} onChange={(v) => upd({ gallery: v })} />
          </SectionCard>
        </TabsContent>
      </Tabs>
    </div>
  );
}
